import type { CustomerInfo, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import {
  ENTITLEMENT_ID,
  NO_ENTITLEMENT,
  type Entitlement,
  type EntitlementSource,
  type PlanId,
  type PremiumPlan,
  type PurchaseOutcome,
  type Storefront,
} from './entitlement';

/**
 * Premium bought in the App Store or Google Play, through RevenueCat. The
 * RevenueCat user is Costline's own user id (the same on every device of a
 * signed-in account), so a purchase follows the account and the server can
 * be told who has Premium.
 *
 * The public SDK keys come from the build (VITE_REVENUECAT_IOS_KEY,
 * VITE_REVENUECAT_ANDROID_KEY); without one the store is not asked, the
 * answer is "not Premium" and there is nothing on sale. The SDK is loaded
 * only on a phone.
 */
export function revenueCatKey(platform: 'ios' | 'android'): string | null {
  const key = platform === 'ios' ? import.meta.env.VITE_REVENUECAT_IOS_KEY : import.meta.env.VITE_REVENUECAT_ANDROID_KEY;
  return typeof key === 'string' && key.trim() ? key.trim() : null;
}

export function entitlementOf(info: Pick<CustomerInfo, 'entitlements'>): Entitlement {
  const e = info.entitlements.active[ENTITLEMENT_ID];
  if (!e?.isActive) return NO_ENTITLEMENT;
  return { active: true, expiresAt: e.expirationDateMillis ?? null, source: 'store', willRenew: e.willRenew };
}

/** The offering's packages as plans: monthly and annual (other packages are not Costline's). */
export function plansOf(packages: readonly Pick<PurchasesPackage, 'packageType' | 'product'>[]): PremiumPlan[] {
  const plan = (id: PlanId, type: string): PremiumPlan[] => {
    const p = packages.find((x) => x.packageType === type);
    if (!p) return [];
    const { priceString, price, currencyCode, pricePerMonthString } = p.product;
    return [{ id, price: priceString, amount: price, currency: currencyCode, perMonth: id === 'yearly' ? (pricePerMonthString ?? null) : null }];
  };
  return [...plan('yearly', 'ANNUAL'), ...plan('monthly', 'MONTHLY')];
}

/** RevenueCat error codes (PURCHASES_ERROR_CODE): 1 cancelled by the person, 20 waiting for approval. */
export function outcomeOfError(e: unknown): PurchaseOutcome {
  const err = e as { code?: string | number; userCancelled?: boolean | null } | null;
  if (err?.userCancelled || String(err?.code) === '1') return { ok: false, reason: 'cancelled' };
  if (String(err?.code) === '20') return { ok: false, reason: 'pending' };
  return { ok: false, reason: 'failed' };
}

export function revenueCat(apiKey: string | null): { entitlement: EntitlementSource; storefront: Storefront } {
  type Purchases = typeof import('@revenuecat/purchases-capacitor').Purchases;
  let ready: Promise<Purchases> | null = null;
  let appUserID: string | null = null;
  // The storefront uses whoever the entitlement check last saw (PremiumProvider checks first).
  let lastUserId: string | null = null;

  const purchases = (userId: string): Promise<Purchases> => {
    ready ??= (async () => {
      const { Purchases } = await import('@revenuecat/purchases-capacitor');
      await Purchases.configure({ apiKey: apiKey!, appUserID: userId });
      appUserID = userId;
      return Purchases;
    })().catch((e: unknown) => {
      ready = null;
      throw e;
    });
    return ready;
  };
  const current = () => (apiKey && lastUserId ? purchases(lastUserId) : Promise.reject(new Error('premium: store not ready')));

  const entitlement: EntitlementSource = {
    async check(userId) {
      if (!apiKey) return NO_ENTITLEMENT;
      lastUserId = userId;
      try {
        const Purchases = await purchases(userId);
        // Signed in on this device since: the purchase moves to the account's id.
        if (appUserID !== userId) {
          const { customerInfo } = await Purchases.logIn({ appUserID: userId });
          appUserID = userId;
          return entitlementOf(customerInfo);
        }
        const { customerInfo } = await Purchases.getCustomerInfo();
        return entitlementOf(customerInfo);
      } catch (e) {
        console.warn('[premium] store check failed', e);
        return NO_ENTITLEMENT;
      }
    },
    subscribe(onChange) {
      if (!apiKey || !ready) return () => {};
      let id: string | null = null;
      let gone = false;
      void ready
        .then(async (Purchases) => {
          const callbackId = await Purchases.addCustomerInfoUpdateListener((info) => onChange(entitlementOf(info)));
          if (gone) void Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: callbackId });
          else id = callbackId;
        })
        .catch(() => {});
      return () => {
        gone = true;
        if (id) void ready?.then((Purchases) => Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: id! }));
      };
    },
  };

  const packageFor = async (Purchases: Purchases, plan: PlanId) => {
    const { current: offering } = await Purchases.getOfferings();
    return offering?.availablePackages.find((p) => p.packageType === (plan === 'yearly' ? 'ANNUAL' : 'MONTHLY')) ?? null;
  };

  const storefront: Storefront = {
    async plans() {
      try {
        const Purchases = await current();
        const { current: offering } = await Purchases.getOfferings();
        return plansOf(offering?.availablePackages ?? []);
      } catch (e) {
        console.warn('[premium] plans', e);
        return [];
      }
    },
    async buy(plan) {
      try {
        const Purchases = await current();
        const aPackage = await packageFor(Purchases, plan);
        if (!aPackage) return { ok: false, reason: 'failed' };
        const { customerInfo } = await Purchases.purchasePackage({ aPackage });
        const e = entitlementOf(customerInfo);
        // Paid but the entitlement is not there yet (a store delay): not a failure, but not Premium either.
        return e.active ? { ok: true, entitlement: e } : { ok: false, reason: 'pending' };
      } catch (e) {
        const outcome = outcomeOfError(e);
        if (outcome.ok === false && outcome.reason === 'failed') console.warn('[premium] purchase', e);
        return outcome;
      }
    },
    async restore() {
      try {
        const Purchases = await current();
        const { customerInfo } = await Purchases.restorePurchases();
        return entitlementOf(customerInfo);
      } catch (e) {
        console.warn('[premium] restore', e);
        return NO_ENTITLEMENT;
      }
    },
    async manageUrl() {
      try {
        const Purchases = await current();
        return (await Purchases.getCustomerInfo()).customerInfo.managementURL ?? null;
      } catch {
        return null;
      }
    },
  };

  return { entitlement, storefront };
}
